import React from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { TableCell, TableRow } from '@/components/ui/table'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import useModalStore from '@/store/useModalStore'
import CustomSheet from '@/components/shared/Sheet/Sheet'
import { Check, Copy, Edit, ExternalLink, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import Badge from '@/components/shared/Badge/Badge'
import ShoppingCard from '@/assets/icons/ShoppingCard'
import CreateCheckout from './Partials/CreateCheckout'
import { CheckoutsService } from '@/services/checkouts.service'
import EditCheckout from './Partials/EditCheckout'
import { Checkout } from '@/@types/Checkouts/list-checkouts'
import { Image } from 'lucide-react'
import TableListCustom from '@/components/shared/TableListCustom/TableListCustom'
import CTATable from '@/components/shared/TableListCustom/CTATable'
import { ProductDetailsResponse, ProductRole } from '@/@types/Products'
import { copyToClipboard } from '@/utils/copyToClipboard'
import { formatCheckoutDisplayName } from '@/utils/formatCheckoutDisplayName'
import { recurringIntervalSuffix } from '@/utils/formatOfferRecurringLabel'
import { usePermissions } from '@/auth/PermissionsContext'

export default function Checkouts() {
  const { modals, openModal, closeModal } = useModalStore()
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation(['checkouts', 'common'])
  const [currentPage, setCurrentPage] = React.useState(1)
  const navigate = useNavigate()
  const [checkout, setCheckout] = React.useState<Checkout>()
  const { product } = useOutletContext<{ product: ProductDetailsResponse }>()

  const { hasPermission } = usePermissions()
  const isProducer = product?.relationship?.role === ProductRole.PRODUCER
  const canCreateCheckout =
    isProducer && hasPermission('checkouts.create_checkout')
  const canEditCheckout = isProducer && hasPermission('checkouts.edit_checkout')
  const canCustomizeCheckout =
    isProducer && hasPermission('checkouts.customize_checkout')

  const { data: checkouts, isPending: isLoadingCheckouts } = useQuery({
    queryKey: [`checkouts-${id}`, currentPage],
    queryFn: async () => {
      if (!id) {
        throw new Error('Product id is required')
      }

      return CheckoutsService.getCheckouts({ productId: id, page: currentPage })
    },
    enabled: Boolean(id),
    staleTime: 1000 * 60 * 60 * 24,
  })

  const { data: offersOptions } = useQuery({
    queryKey: [`offers-options-${id}`],
    queryFn: async () => {
      if (!id) {
        throw new Error('Product id is required')
      }

      return CheckoutsService.getOffers({
        productId: id,
        page: currentPage,
      })
    },
    enabled: Boolean(id),
    staleTime: 1000 * 60 * 60 * 24,
  })

  const headers = [
    {
      title: t('common:name_single'),
      key: 'invoice',
    },
    {
      title: t('common:link'),
      key: 'value',
    },
    {
      title: t('common:offer'),
      key: 'affiliates',
    },
    {
      title: t('common:status'),
      key: 'status',
    },
    {
      title: '',
      key: 'CTA',
    },
  ]

  const [copiedCheckoutId, setCopiedCheckoutId] = React.useState<string | null>(
    null,
  )
  async function handleCopyToClipboard(url: string, checkoutId: string) {
    await copyToClipboard(url)
    setCopiedCheckoutId(checkoutId)
    setTimeout(() => {
      setCopiedCheckoutId(null)
    }, 2000)
  }

  return (
    <>
      {canCreateCheckout && (
        <CustomSheet
          open={modals['create-checkout']}
          onClose={() => closeModal('create-checkout')}
        >
          <CreateCheckout
            offers={offersOptions ?? []}
            modalName="create-checkout"
          />
        </CustomSheet>
      )}
      {canEditCheckout && checkout && (
        <CustomSheet
          open={modals['edit-checkout']}
          onClose={() => closeModal('edit-checkout')}
        >
          <EditCheckout
            checkout={checkout}
            offers={offersOptions ?? []}
            modalName="edit-checkout"
          />
        </CustomSheet>
      )}

      <Card className="w-full mt-8 bg-[#09090b] overflow-x-auto">
        <div className="flex md:items-center gap-3 flex-col md:flex-row w-full p-4 justify-between">
          <h1 className="text-base font-semibold">{t('checkouts')}</h1>
          {canCreateCheckout && (
            <Button
              data-cy="btn-create-checkout"
              className="w-full md:max-w-[155px]"
              onClick={() => openModal('create-checkout')}
            >
              <Plus />
              {t('create_checkout')}
            </Button>
          )}
        </div>
        <TableListCustom
          emptyDescriptionMessage={t('emptyCheckouts.description')}
          emptyIconMessage={<ShoppingCard />}
          emptyTitleMessage={t('emptyCheckouts.title')}
          headers={headers}
          isLoadingData={isLoadingCheckouts}
          data={checkouts ?? { data: [] }}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        >
          {checkouts?.data?.map((checkout) => (
            <TableRow
              key={checkout.id}
              className="h-[64px] cursor-normal border-b border-b-colorBorder hover:bg-transparent"
              data-cy="checkout-table-row"
            >
              <TableCell className="font-medium">{checkout.name}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <div className="inline-block cursor-pointer w-auto">
                    <a
                      href={checkout?.link}
                      target="_blank"
                      className="bg-[#27272a] flex items-center gap-4 w-full rounded-md p-1"
                    >
                      {checkout?.link}
                      <ExternalLink
                        className="cursor-pointer text-muted"
                        width={18}
                        height={18}
                      />
                    </a>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-2 h-8 w-8"
                    onClick={() =>
                      handleCopyToClipboard(checkout.link, String(checkout.id))
                    }
                  >
                    {copiedCheckoutId === String(checkout.id) ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </TableCell>
              <TableCell>
                <article className="max-w-[220px]">
                  <p
                    className="truncate"
                    title={formatCheckoutDisplayName(
                      product?.name,
                      checkout?.offer?.name,
                    )}
                  >
                    {formatCheckoutDisplayName(
                      product?.name,
                      checkout?.offer?.name,
                    )}
                  </p>
                  <p className="text-muted mt-1">
                    {checkout?.offer?.price_formatted}
                    {recurringIntervalSuffix(
                      checkout.offer?.payment_type,
                      checkout.offer?.recurring_interval,
                    )}
                  </p>
                </article>
              </TableCell>
              <TableCell className="h-full">
                <div className="flex items-center">
                  <Badge status={checkout.is_active ? 'active' : 'inactive'} />
                </div>
              </TableCell>
              <TableCell>
                {(canCustomizeCheckout || canEditCheckout) && (
                  <CTATable
                    items={[
                      ...(canCustomizeCheckout
                        ? [
                            {
                              id: 3,
                              label: t('customize'),
                              icon: <Image />,
                              action: () => {
                                navigate(
                                  `/products/edit-product/${id}/checkouts/${checkout.id}`,
                                )
                              },
                            },
                          ]
                        : []),
                      ...(canEditCheckout
                        ? [
                            {
                              id: 1,
                              label: t('edit'),
                              icon: <Edit />,
                              action: () => {
                                setCheckout(checkout)
                                openModal('edit-checkout')
                              },
                            },
                          ]
                        : []),
                    ]}
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableListCustom>
      </Card>
    </>
  )
}
